import { useCallback, useEffect, useState } from "react";
import { fetchPaymentConfig, updatePaymentConfig } from "./paymentsApi";

/**
 * Normalizes the API response into a config object with safe defaults.
 * @param {*} data - Decrypted API payload
 * @returns {Object|null}
 */
function normalizeConfig(data) {
    if (!data || typeof data !== "object") return null;
    return {
        card_threshold: Number(data.card_threshold) || 0,
        currency: data.currency || "eur",
        hold_expiration_hours: Number(data.hold_expiration_hours) || 72,
        allowed_methods_low: Array.isArray(data.allowed_methods_low) ? data.allowed_methods_low : [],
        allowed_methods_high: Array.isArray(data.allowed_methods_high) ? data.allowed_methods_high : [],
        updated_by_admin_id: data.updated_by_admin_id ?? null,
    };
}

/**
 * Helper that runs the GET request inside an aborted-aware wrapper.
 * @param {Object} args - Loader arguments
 */
async function loadConfig({ signal, setConfig, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchPaymentConfig();
        if (!signal.aborted) setConfig(normalizeConfig(data));
    } catch (err) {
        if (!signal.aborted) setError(err?.response?.data || "Impossible de charger la configuration");
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook to fetch and update the payment configuration.
 * @returns {{ config: Object|null, loading: boolean, error: any, save: Function, refresh: Function }}
 */
function usePaymentsConfig() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        loadConfig({ signal: controller.signal, setConfig, setError, setLoading });
        return () => controller.abort();
    }, [tick]);

    const save = useCallback(async (payload) => {
        const updated = await updatePaymentConfig(payload);
        setConfig(normalizeConfig(updated));
        return updated;
    }, []);

    const refresh = useCallback(() => setTick((t) => t + 1), []);

    return { config, loading, error, save, refresh };
}

export default usePaymentsConfig;
